import { useRef, useCallback } from 'react';
import { Card, Stack, useTheme, spacing } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import type { Message, MessageCreateRequest, MessageUpdateRequest } from '../../../types/media-sim';

interface MessageFormProps {
  message?: Message;
  onSave: (data: MessageCreateRequest | MessageUpdateRequest) => void;
  saving?: boolean;
}

export function MessageForm({ message, onSave, saving }: MessageFormProps) {
  const { theme } = useTheme();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const isRegistered = message?.status === 'registered';

  const debouncedSave = useCallback(
    (data: MessageCreateRequest | MessageUpdateRequest) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSave(data), 500);
    },
    [onSave]
  );

  const nameRef = useRef(message?.message_name || '');
  const bodyRef = useRef(message?.body || '');

  const handleNameChange = (value: string) => {
    nameRef.current = value;
    if (!isRegistered) {
      debouncedSave({ message_name: value, body: bodyRef.current });
    }
  };

  const handleBodyChange = (value: string) => {
    bodyRef.current = value;
    if (!isRegistered) {
      debouncedSave({ message_name: nameRef.current, body: value });
    }
  };

  const inputStyle = {
    width: '100%',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: '4px',
    backgroundColor: isRegistered ? theme.colors.background.elevated : theme.colors.background.paper,
    color: theme.colors.text.primary,
    fontSize: '0.9rem',
  };

  return (
    <Card variant="elevated">
      <Card.Body>
        <Stack gap="md">
          {message && (
            <StatusBadge status={message.status} size="md" />
          )}

          <div>
            <label style={{ display: 'block', marginBottom: spacing.xs, fontSize: '0.85rem', fontWeight: 500, color: theme.colors.text.primary }}>
              Message Name
            </label>
            <input
              type="text"
              defaultValue={message?.message_name || ''}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={isRegistered}
              placeholder="Enter message name"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: spacing.xs, fontSize: '0.85rem', fontWeight: 500, color: theme.colors.text.primary }}>
              Message Body
            </label>
            <textarea
              defaultValue={message?.body || ''}
              onChange={(e) => handleBodyChange(e.target.value)}
              disabled={isRegistered}
              placeholder="Enter message body"
              rows={12}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {saving && (
            <p style={{ fontSize: '0.8rem', color: theme.colors.text.secondary, margin: 0 }}>
              Saving...
            </p>
          )}

          {isRegistered && message?.registered_at && (
            <p style={{ fontSize: '0.8rem', color: theme.colors.text.secondary, margin: 0 }}>
              Registered on {new Date(message.registered_at).toLocaleString()}
            </p>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
}
